import type { CallbackEvent } from "@shopify/polaris-types";
import {
  CollapsibleSectionCard,
  SectionHeader,
  NumericField,
} from "../../../components/shared/EditorShared";
import type { BundleEditorGroup } from "../../../store/bundle-editor.mapping";
import {
  openBundleResourcePicker,
  type BundleVariantPick,
} from "../../../utils/resource-picker.utils";

// ─── Section 3b: Configurable groups ──────────────────────────────────────────

export function ConfigurableGroupsSection({
  groups,
  onAddGroup,
  onRemoveGroup,
  onUpdateGroup,
  onAddOption,
  onRemoveOption,
}: {
  groups: BundleEditorGroup[];
  onAddGroup: () => void;
  onRemoveGroup: (groupIndex: number) => void;
  onUpdateGroup: <K extends keyof BundleEditorGroup>(
    groupIndex: number,
    key: K,
    value: BundleEditorGroup[K]
  ) => void;
  onAddOption: (groupIndex: number, picks: BundleVariantPick[]) => void;
  onRemoveOption: (groupIndex: number, optionIndex: number) => void;
}) {
  return (
    <CollapsibleSectionCard
      header={<SectionHeader number={4} title="Grupos del bundle" required />}
    >
      <s-stack direction="block" gap="base">
        <s-text color="subdued">
          Crea uno o más grupos. El cliente elegirá una cantidad determinada de
          opciones dentro de cada grupo.
        </s-text>

        {groups.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-6 flex flex-col items-center gap-3 text-center">
            <s-icon type="product" />
            <s-text color="subdued">No has agregado grupos todavía.</s-text>
            <s-button variant="primary" icon="plus" onClick={onAddGroup}>
              Crear primer grupo
            </s-button>
          </div>
        ) : (
          <s-stack direction="block" gap="base">
            {groups.map((group, gi) => (
              <GroupEditor
                key={group._tempId}
                group={group}
                onRemoveGroup={() => onRemoveGroup(gi)}
                onUpdateGroup={(key, value) => onUpdateGroup(gi, key, value)}
                onAddOption={(picks) => onAddOption(gi, picks)}
                onRemoveOption={(oi) => onRemoveOption(gi, oi)}
              />
            ))}
            <s-button variant="tertiary" icon="plus" onClick={onAddGroup}>
              Agregar otro grupo
            </s-button>
          </s-stack>
        )}
      </s-stack>
    </CollapsibleSectionCard>
  );
}

// ─── Group editor card ────────────────────────────────────────────────────────

function GroupEditor({
  group,
  onRemoveGroup,
  onUpdateGroup,
  onAddOption,
  onRemoveOption,
}: {
  group: BundleEditorGroup;
  onRemoveGroup: () => void;
  onUpdateGroup: <K extends keyof BundleEditorGroup>(
    key: K,
    value: BundleEditorGroup[K]
  ) => void;
  onAddOption: (picks: BundleVariantPick[]) => void;
  onRemoveOption: (optionIndex: number) => void;
}) {
  const handlePick = () => {
    openBundleResourcePicker(
      (picks) => onAddOption(picks),
      () => {
        window.shopify?.toast?.show(
          "No se pudo abrir el selector de productos.",
          { isError: true }
        );
      }
    );
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 grid grid-cols-[1fr_140px] gap-3">
          <s-text-field
            label="Nombre del grupo"
            value={group.name}
            onInput={(e: CallbackEvent<"s-text-field">) =>
              onUpdateGroup("name", e.currentTarget.value)
            }
          />
          <NumericField
            label="Elegir (cantidad)"
            value={group.pickCount}
            min={1}
            onChange={(v) => onUpdateGroup("pickCount", v)}
          />
        </div>
        <button
          onClick={onRemoveGroup}
          className="p-1.5 mt-6 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
          aria-label="Eliminar grupo"
        >
          <s-icon type="delete" />
        </button>
      </div>

      {group.options.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-4 flex flex-col items-center gap-2 text-center">
          <s-text color="subdued">
            No hay opciones en este grupo todavía.
          </s-text>
          <s-button variant="primary" icon="plus" onClick={handlePick}>
            Agregar opciones
          </s-button>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-[48px_1fr_40px] bg-gray-50 border-b border-gray-200 px-3 py-2 items-center">
            <s-text type="strong" />
            <s-text type="strong">Opción</s-text>
            <s-text type="strong" />
          </div>

          {group.options.map((option, oi) => (
            <div
              key={option._tempId}
              className="grid grid-cols-[48px_1fr_40px] items-center gap-3 px-3 py-3 border-b border-gray-100 last:border-b-0"
            >
              {option.imageUrl ? (
                <img
                  src={option.imageUrl}
                  alt=""
                  className="w-10 h-10 rounded object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center">
                  <s-icon type="image" />
                </div>
              )}

              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {option.productTitle || "Producto"}
                </p>
                {option.variantTitle && (
                  <p className="text-xs text-gray-500 truncate">
                    {option.variantTitle}
                  </p>
                )}
              </div>

              <button
                onClick={() => onRemoveOption(oi)}
                className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                aria-label="Quitar opción"
              >
                <s-icon type="delete" />
              </button>
            </div>
          ))}
        </div>
      )}

      {group.options.length > 0 && (
        <s-button variant="tertiary" icon="plus" onClick={handlePick}>
          Agregar más opciones
        </s-button>
      )}
    </div>
  );
}
